import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { COUNTRIES } from "@/data/site";
import { NAV } from "@/constants/testIds";
import { useT } from "@/i18n";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Header() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [countriesOpen, setCountriesOpen] = useState(false);
  useLocation();

  const NAV_LINKS = [
    { to: "/", label: t.nav.home, id: NAV.home },
    { to: "/services", label: t.nav.services, id: NAV.services },
    { to: "/ai-transformation", label: t.nav.aiTransformation, id: NAV.aiTransformation },
    { to: "/industries", label: t.nav.industries, id: NAV.industries },
    { to: "/about", label: t.nav.about, id: NAV.about },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-white/[0.06] bg-[#071A35]/75 backdrop-blur-xl">
      <div className="nx-container flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid={NAV.brand}
          className="flex items-center gap-3 group"
          onClick={() => setOpen(false)}
        >
          <BrandMark />
          <div className="leading-tight">
            <div className="nx-heading text-white font-bold tracking-tight text-[17px]">
              NEXORA
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">
              Technologies
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-md transition-colors ${
                  isActive ? "text-white" : "text-white/70 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div
            className="relative"
            onMouseEnter={() => setCountriesOpen(true)}
            onMouseLeave={() => setCountriesOpen(false)}
          >
            <button
              data-testid={NAV.countriesTrigger}
              className="px-3 py-2 text-sm rounded-md text-white/70 hover:text-white inline-flex items-center gap-1"
            >
              {t.nav.countries}
              <ChevronDown className="w-4 h-4" />
            </button>
            {countriesOpen && (
              <div className="absolute right-0 top-full pt-2 w-64">
                <div className="nx-card rounded-xl p-2 shadow-2xl">
                  {COUNTRIES.map((c) => {
                    const key = c.code === "uk" ? "united-kingdom" : c.code === "nl" ? "netherlands" : c.code === "de" ? "germany" : c.code === "ch" ? "switzerland" : "belgium";
                    return (
                      <Link
                        key={c.code}
                        to={c.path}
                        data-testid={`nav-country-${c.code}`}
                        className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-white/5 text-sm text-white/85"
                      >
                        <span className="flex items-center gap-3">
                          <span className="text-base leading-none">{c.flag}</span>
                          <span>{t.countriesNav[key]}</span>
                        </span>
                        <ArrowUpRight className="w-4 h-4 text-white/40" />
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>
          <Link
            to="/contact"
            data-testid={NAV.ctaHeader}
            className="hidden md:inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-md bg-[#0057FF] hover:bg-[#2F80FF] text-white transition-all nx-cta-glow"
          >
            {t.nav.bookStrategy}
            <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            className="lg:hidden p-2 rounded-md text-white/85 hover:bg-white/5"
            onClick={() => setOpen(!open)}
            data-testid={NAV.mobileToggle}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#071A35]/95 backdrop-blur-xl">
          <div className="nx-container py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`${l.id}-mobile`}
                className="px-2 py-3 text-sm text-white/85"
              >
                {l.label}
              </NavLink>
            ))}
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 mt-3 px-2">
              {t.nav.countries}
            </div>
            {COUNTRIES.map((c) => {
              const key = c.code === "uk" ? "united-kingdom" : c.code === "nl" ? "netherlands" : c.code === "de" ? "germany" : c.code === "ch" ? "switzerland" : "belgium";
              return (
                <Link
                  key={c.code}
                  to={c.path}
                  onClick={() => setOpen(false)}
                  className="px-2 py-2 text-sm text-white/80 flex items-center gap-3"
                >
                  <span>{c.flag}</span>
                  {t.countriesNav[key]}
                </Link>
              );
            })}
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 mt-3 px-2">
              {t.langSwitcher.label}
            </div>
            <LanguageSwitcher variant="mobile" />
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex items-center justify-center gap-2 text-sm font-medium px-4 py-3 rounded-md bg-[#0057FF] text-white"
            >
              {t.nav.bookStrategy}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function BrandMark() {
  return (
    <div className="relative w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#0057FF] to-[#38D8FF] shadow-[0_4px_18px_-4px_rgba(56,216,255,0.6)]">
      <svg viewBox="0 0 36 36" className="w-6 h-6 text-white" fill="none">
        <path
          d="M8 28V8h3.5l13 14V8H28v20h-3.5l-13-14v14H8z"
          fill="currentColor"
        />
      </svg>
    </div>
  );
}
